if (!customElements.get('product-hnv2-thumbnail-rail')) {
  customElements.define(
    'product-hnv2-thumbnail-rail',
    class ProductHnv2ThumbnailRail extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[id^="Slider-Thumbnails"]');
        this.previousButton = this.querySelector('[data-thumbnail-previous]');
        this.nextButton = this.querySelector('[data-thumbnail-next]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.previousButton?.addEventListener('click', this.showPrevious);
        this.nextButton?.addEventListener('click', this.showNext);
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener('click', this.showPrevious);
        this.nextButton?.removeEventListener('click', this.showNext);
      }

      showPrevious = () => {
        this.showByOffset(-1);
      };

      showNext = () => {
        this.showByOffset(1);
      };

      showByOffset(offset) {
        const thumbnails = [...this.querySelectorAll('[data-target]')].filter((thumbnail) => thumbnail.clientWidth > 0);
        if (thumbnails.length < 2) return;

        const activeIndex = Math.max(
          0,
          thumbnails.findIndex((thumbnail) =>
            thumbnail.querySelector('.product-hnv2-gallery__thumbnail[aria-current="true"]')
          )
        );
        const targetIndex = (activeIndex + offset + thumbnails.length) % thumbnails.length;
        const target = thumbnails[targetIndex];

        target.querySelector('.product-hnv2-gallery__thumbnail')?.click();
        requestAnimationFrame(() => this.centerThumbnail(target));
      }

      centerThumbnail(thumbnail) {
        if (!this.slider || !thumbnail) return;
        const centeredPosition = thumbnail.offsetLeft - (this.slider.clientWidth - thumbnail.clientWidth) / 2;
        this.slider.scrollTo({
          left: Math.max(0, centeredPosition),
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      isSlideVisible(thumbnail, offset = 0) {
        if (!this.slider || !thumbnail) return false;
        const visibleStart = this.slider.scrollLeft + offset;
        const visibleEnd = this.slider.scrollLeft + this.slider.clientWidth - offset;
        return thumbnail.offsetLeft >= visibleStart && thumbnail.offsetLeft + thumbnail.clientWidth <= visibleEnd;
      }
    }
  );
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          expandButton: this.querySelector('[data-gallery-expand]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        const initialMedia =
          this.elements.viewer.querySelector('.product-hnv2-gallery__slide.is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');
        this.syncExpandControl(initialMedia);
        this.prepareMedia(initialMedia);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.syncExpandControl(event.detail.currentElement);
        this.prepareMedia(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.syncExpandControl(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.prepareMedia(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        // Active variant media changes which slides participate in the rail.
        // Rebuild the visible page map before scrolling or emitting slideChanged.
        if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Mobile variant selection updates the active slide without pulling
          // the shopper away from the option and purchase controls.
          if (!this.mql.matches) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      syncExpandControl(activeMedia) {
        if (!this.elements.expandButton || !activeMedia) return;

        const mediaToggle = activeMedia.querySelector('.product__media-toggle[data-media-id]');
        if (!mediaToggle) return;

        this.elements.expandButton.dataset.mediaId = mediaToggle.dataset.mediaId;

        const sourceLabel = mediaToggle.querySelector('.visually-hidden');
        const targetLabel = this.elements.expandButton.querySelector('.visually-hidden');
        if (sourceLabel && targetLabel) targetLabel.textContent = sourceLabel.textContent;
      }

      prepareMedia(mediaItem) {
        mediaItem?.querySelector('hnv2-media-status')?.prepare?.();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('.product-hnv2-gallery__thumbnail')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete && image.naturalWidth > 0) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
